import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Router } from '@angular/router';
import { Subscription, map } from 'rxjs';
import { CityItem, CityService, isBackendCityId, SelectedCity } from '../services/city.service';
import { LocationUpdateModalService } from '../services/location-update-modal.service';
import { AuthService } from '../services/auth.service';
import { DataService } from '../services/data.service';
import { ThemeService } from '../services/theme.service';

interface FooterCategory {
  id?: number | string;
  label: string;
  slug: string;
}

const FOOTER_CATEGORY_LIMIT = 5;

@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.css'
})
export class FooterComponent implements OnInit, OnDestroy {
  city: string = '';
  footerCities: CityItem[] = [];
  discoverCategories: FooterCategory[] = [
    { id: 'concerts', label: 'Concerts', slug: 'concerts' },
    { id: 'expos', label: 'Expos & Trade Shows', slug: 'expose' },
    { id: 'conferences', label: 'Conferences', slug: 'conferences' },
    { id: 'music-festivals', label: 'Music Festivals', slug: 'music-festivals' },
    { id: 'workshops', label: 'Workshops', slug: 'workshops' }
  ];
  private citySubscription?: Subscription;

  constructor(
    private cityService: CityService,
    private router: Router,
    private locationUpdateModalService: LocationUpdateModalService,
    private authService: AuthService,
    private dataService: DataService,
    private themeService: ThemeService,
  ) { }

  get isDarkMode(): boolean {
    return this.themeService.isDarkMode();
  }

  learnMoreLinks = [
    'Pricing',
    'How It works',
    'Policies',
    'Privacy',
    'APIs for Developers',
    'Support / FAQs'
  ];

  aboutLinks = [
    'About us',
    'Contact us',
    'Blog',
    'Event Magazine',
    'Product Diary',
    'Sitemap'
  ];

  organizeEventsLinks = [
    'Conferences',
    'Workshops and Trainings',
    'Sports and Fitness Events',
    'Entertainment Events',
    'Treks and Trips'
  ];

  popularSearchesLinks = [
    'Countries supported by Townscript',
    'Sell Event Tickets Online',
    'Event Management Software',
    'Event Registration Software',
    'Conference management System',
    'Event Planning Software'
  ];

  compareUsLinks = [
    'Eventbrite',
    'Peatix',
    'Zoom',
    'Skype',
    'Go To Meeting',
    'Google Meet Alternative',
    'WebEx Alternative',
    'GoToWebinar Alternative'
  ];

  ngOnInit(): void {
    // Keep footer consistent with header: cities come from API.
    this.citySubscription = this.cityService.cities$
      .pipe(
        map((all) => {
          const sorted = [...(all || [])].sort((a, b) => {
            const ea = Number((a as any)?.event_count ?? (a as any)?.events_count ?? 0);
            const eb = Number((b as any)?.event_count ?? (b as any)?.events_count ?? 0);
            return eb - ea;
          });
          return sorted.slice(0, 6);
        })
      )
      .subscribe((cities) => {
        this.footerCities = cities;
      });

    this.cityService.loadCities();
    this.loadDiscoverCategories();

    const selectedCity = localStorage.getItem('livepass_selected_city');

    if (selectedCity) {
      this.city = JSON.parse(selectedCity).name
        .replace(/\u2060/g, '')
        .trim()
        .toLowerCase();
    }
  }

  ngOnDestroy(): void {
    this.citySubscription?.unsubscribe();
  }

  trackFooterCategory(index: number, category: FooterCategory): number | string {
    return category.id ?? category.slug ?? index;
  }

  trackFooterCity(index: number, city: CityItem): number | string {
    return city.id ?? city.name ?? index;
  }

  pickCity(city: CityItem, ev?: Event): void {
    ev?.preventDefault();

    if (city?.id == null || !city?.name) return;

    const previous = this.cityService.getSelectedCity();
    const oldCityName = previous?.name?.trim() ?? '';

    const next: SelectedCity = {
      id: Number(city.id),
      name: city.name,
      country_name: city.country_name
    };

    const finish = (showModal: boolean) => {
      this.cityService.setSelectedCity(next);


      if (showModal && oldCityName) {
        this.locationUpdateModalService.show(oldCityName, next.name);
      }

      const url = this.router.url.split('?')[0].split('#')[0];
      if (url !== '/' && url !== '') {
        void this.router.navigateByUrl('/');
      }
    };

    if (!this.authService.isAuthenticated()) {
      finish(!!oldCityName);
      return;
    }

    const payload: Record<string, unknown> = {
      city: next.name
    };

    if (isBackendCityId(next.id)) {
      payload['city_id'] = next.id;
    }

    this.dataService.postProfile(payload).subscribe({
      next: () => {
        const profile = this.authService.getUserProfile();
        if (profile) {
          this.authService.setUserProfile({
            ...profile,
            city: next.name,
            city_id: isBackendCityId(next.id) ? next.id : profile.city_id
          });
        }
        finish(!!oldCityName);
      },
      error: () => {
        finish(false);
      }
    });
  }

  private loadDiscoverCategories(): void {
    this.dataService.getCategories(undefined, 0).subscribe({
      next: (response) => {
        const categories = this.parseCategories(response)
          .map((category) => this.mapFooterCategory(category))
          .filter((category): category is FooterCategory => !!category)
          .slice(0, FOOTER_CATEGORY_LIMIT);

        if (categories.length) {
          this.discoverCategories = categories;
        }
      },
      error: (err) => {
        console.error('Error loading footer categories:', err);
      }
    });
  }

  private parseCategories(response: any): any[] {
    if (!response) return [];

    if (response.code === 1 && response.data) {
      if (response.data.code === 1 && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      if (response.data.data && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      if (Array.isArray(response.data)) {
        return response.data;
      }
    }

    if (response.data && Array.isArray(response.data)) {
      return response.data;
    }
    if (response.categories && Array.isArray(response.categories)) {
      return response.categories;
    }
    if (Array.isArray(response)) {
      return response;
    }

    return [];
  }

  private mapFooterCategory(category: any): FooterCategory | null {
    const label = String(category?.name || category?.label || category?.slug || '').trim();
    const slug = String(
      category?.slug || category?.name?.toLowerCase().replace(/\s+/g, '-') || ''
    ).trim();

    if (!label || !slug) {
      return null;
    }

    return {
      id: category?.id ?? slug,
      label,
      slug
    };
  }
}
